import {
  Calendar,
  FileText,
  MapPin,
  Building2,
  Phone,
  Mail,
} from 'lucide-react'

import { formatDisplayDate } from '../utils/purchaseOrder.utils'

/**
 * Displays Vendor & Order summary (Date, GST Number, Address) inside Create PO Modal.
 * Updates dynamically when a vendor is selected or passed from the vendor list table.
 */
export function VendorInfoCard({ vendor, date }) {
  const displayDate = formatDisplayDate(date)

  if (!vendor) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50/80 p-4 transition-all">
        <div className="flex items-center gap-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
          <Building2 className="size-4 text-slate-400" />
          <span>Vendor & PO Details</span>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="flex items-start gap-3 rounded-xl bg-white p-3 border border-slate-200/70 shadow-xs">
            <div className="rounded-lg bg-indigo-50 p-2 text-indigo-600">
              <Calendar className="size-4" />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500">PO Date</p>
              <p className="text-sm font-semibold text-slate-800">
                {displayDate}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3 rounded-xl bg-white/70 p-3 border border-slate-200/70 shadow-xs">
            <div className="rounded-lg bg-slate-100 p-2 text-slate-400">
              <FileText className="size-4" />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500">GST Number</p>
              <p className="text-sm italic text-slate-400">
                Select vendor to view
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3 rounded-xl bg-white/70 p-3 border border-slate-200/70 shadow-xs">
            <div className="rounded-lg bg-slate-100 p-2 text-slate-400">
              <MapPin className="size-4" />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500">Address</p>
              <p className="text-sm italic text-slate-400">
                Select vendor to view
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-2xl border border-slate-200/90 bg-gradient-to-br from-slate-50 to-indigo-50/30 p-4.5 shadow-xs transition-all">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200/80 pb-3 mb-3">
        <div className="flex items-center gap-2">
          <div className="flex size-7 items-center justify-center rounded-lg bg-[#080d25] text-white">
            <Building2 className="size-4" />
          </div>
          <div>
            <h4 className="text-sm font-semibold text-[#080d25]">
              {vendor.vendorName || 'Selected Vendor'}
            </h4>
            {(vendor.email || vendor.phoneNumber) && (
              <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                {vendor.email && (
                  <span className="inline-flex items-center gap-1">
                    <Mail className="size-3" />
                    {vendor.email}
                  </span>
                )}
                {vendor.phoneNumber && (
                  <span className="inline-flex items-center gap-1">
                    <Phone className="size-3" />
                    {vendor.phoneNumber}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700 border border-emerald-200/60">
          Vendor Attached
        </span>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {/* Date */}
        <div className="flex items-start gap-3 rounded-xl bg-white p-3 border border-slate-200/70 shadow-xs">
          <div className="rounded-lg bg-indigo-50 p-2 text-indigo-600">
            <Calendar className="size-4" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-medium uppercase tracking-wider text-slate-500">
              PO Date
            </p>
            <p className="text-sm font-semibold text-slate-900 truncate">
              {displayDate}
            </p>
          </div>
        </div>

        {/* GST Number */}
        <div className="flex items-start gap-3 rounded-xl bg-white p-3 border border-slate-200/70 shadow-xs">
          <div className="rounded-lg bg-amber-50 p-2 text-amber-600">
            <FileText className="size-4" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-medium uppercase tracking-wider text-slate-500">
              GST Number
            </p>
            <p className="text-sm font-semibold text-slate-900 font-mono truncate">
              {vendor.gstNumber || (
                <span className="font-sans font-normal text-slate-400">
                  N/A
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Address */}
        <div className="flex items-start gap-3 rounded-xl bg-white p-3 border border-slate-200/70 shadow-xs">
          <div className="rounded-lg bg-blue-50 p-2 text-blue-600">
            <MapPin className="size-4" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-medium uppercase tracking-wider text-slate-500">
              Address
            </p>
            <p className="text-xs text-slate-700 leading-snug line-clamp-2">
              {vendor.address || <span className="text-slate-400">N/A</span>}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
